import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { AnimationOptions } from 'ngx-lottie';
import { MessageService } from 'primeng/api';
import { CourseCreateDto } from 'src/app/aplication/api/Manipulate/CoursesObjects/CourseDto';
import { TeacherDto } from 'src/app/aplication/api/Manipulate/CoursesObjects/TeacherDto';
import { CourseService } from 'src/app/aplication/api/Services/CourseServices/Course.service';
import { COURSE_FORM_VALIDATORS } from 'src/app/aplication/api/Models/Upload.model';

@Component({
  selector: 'app-add-new-course',
  templateUrl: './add-new-course.component.html',
  styleUrl: './add-new-course.component.scss'
})
export class AddNewCourseComponent implements OnInit {

  isPublicOptions: { label: string; value: boolean }[] = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false }
  ];

  formCourse!: FormGroup;
  loaded: boolean = false;
  submitting: boolean = false;

  animationLoading: AnimationOptions = {
    path: '../../../../../../../../assets/Animations/animationLoader.json',
  };

  private _listTeachers: TeacherDto[] = [];
  public get listTeachers(): TeacherDto[] {
    return this._listTeachers;
  }
  @Input() public set listTeachers(v: TeacherDto[]) {
    this._listTeachers = v;
  }

  @Output() showDialogCreate: EventEmitter<boolean> = new EventEmitter();
  @Output() courseCreated: EventEmitter<CourseCreateDto> = new EventEmitter();

  constructor(
    protected _courseService: CourseService,
    protected _messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loaded = true;
  }

  private initForm(): void {
    this.formCourse = new FormGroup({
      title: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(200)
      ]),
      description: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(2000)
      ]),
      teacherId: new FormControl('', [
        Validators.required
      ]),
      price: new FormControl(null, [
        Validators.required,
        Validators.min(0)
      ]),
      isPublic: new FormControl(false)
    });
  }

  /**
   * Retorna mensagens de erro para o campo title
   */
  get titleError(): string {
    const control = this.formCourse.get('title');
    if (!control || !control.errors || !this.formCourse.touched) return '';

    if (control.errors['required']) return COURSE_FORM_VALIDATORS.title.required;
    if (control.errors['minlength']) return COURSE_FORM_VALIDATORS.title.minlength;
    if (control.errors['maxlength']) return COURSE_FORM_VALIDATORS.title.maxlength;
    return '';
  }

  /**
   * Retorna mensagens de erro para o campo description
   */
  get descriptionError(): string {
    const control = this.formCourse.get('description');
    if (!control || !control.errors || !this.formCourse.touched) return '';

    if (control.errors['required']) return COURSE_FORM_VALIDATORS.description.required;
    if (control.errors['minlength']) return COURSE_FORM_VALIDATORS.description.minlength;
    if (control.errors['maxlength']) return COURSE_FORM_VALIDATORS.description.maxlength;
    return '';
  }

  /**
   * Retorna mensagens de erro para o campo teacherId
   */
  get teacherIdError(): string {
    const control = this.formCourse.get('teacherId');
    if (!control || !control.errors || !this.formCourse.touched) return '';

    if (control.errors['required']) return COURSE_FORM_VALIDATORS.teacherId.required;
    return '';
  }

  /**
   * Retorna mensagens de erro para o campo price
   */
  get priceError(): string {
    const control = this.formCourse.get('price');
    if (!control || !control.errors || !this.formCourse.touched) return '';

    if (control.errors['required']) return COURSE_FORM_VALIDATORS.price.required;
    if (control.errors['min']) return COURSE_FORM_VALIDATORS.price.min;
    return '';
  }

  /**
   * Verifica se o formulário é válido
   */
  get isFormValid(): boolean {
    return this.formCourse?.valid ?? false;
  }

  CreateCourse(): void {
    if (this.submitting) return;

    // Marca todos os campos como touched para mostrar erros
    this.formCourse.markAllAsTouched();

    if (!this.formCourse.valid) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha todos os campos corretamente',
        life: 3000
      });
      return;
    }

    this.submitting = true;
    const formValue = this.formCourse.value;

    const dto = new CourseCreateDto();
    dto.title = formValue.title.trim();
    dto.description = formValue.description.trim();
    dto.teacherId = formValue.teacherId;
    dto.price = formValue.price;
    dto.isPublic = formValue.isPublic;

    this._courseService.AddCourse(dto).subscribe({
      next: (response) => {
        this.submitting = false;
        this._messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Curso criado com sucesso!',
          life: 3000
        });
        this.courseCreated.emit(dto);
        this.resetForm();
        this.showDialogCreate.emit(false);
      },
      error: (error) => {
        this.submitting = false;
        this._messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: this.extractErrorMessage(error),
          life: 4000
        });
      }
    });
  }

  /**
   * Reseta o formulário após criação bem sucedida
   */
  private resetForm(): void {
    this.formCourse.reset({
      title: '',
      description: '',
      teacherId: '',
      price: null,
      isPublic: false
    });
  }

  /**
   * Extrai mensagem de erro da resposta HTTP
   */
  private extractErrorMessage(error: any): string {
    if (error.error?.message) {
      return error.error.message;
    }
    if (error.error?.errors) {
      const errors = error.error.errors;
      if (Array.isArray(errors)) {
        return errors.join(', ');
      }
      const firstKey = Object.keys(errors)[0];
      if (firstKey && errors[firstKey]) {
        return errors[firstKey][0] || 'Erro de validação';
      }
    }
    return 'Erro ao criar curso. Tente novamente.';
  }
}
